import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { t } from 'i18next';
import React from 'react';
import { Button } from 'react-bootstrap';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

export type FormMode = 'read' | 'edit';

type EditableFormShellProps<TValues> = {
  form: UseFormReturn<TValues>;

  /** Baseline values used for reset/cancel. For "create new", pass defaults. */
  initialValues: TValues;

  /** External deps loaded + anything else required to allow editing. */
  canEdit: boolean;
  canCancel?: boolean;

  /** Optional saving flag to disable the form while submitting. */
  isSaving?: boolean;

  /** toggle = read/edit */
  mode?: FormMode;

  /** Labels (optional) */
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;

  /** Called on valid submit */
  onSubmit: (values: TValues) => Promise<void> | void;

  children: (args: { mode: FormMode; canEdit: boolean }) => React.ReactNode;
};

export const EditableFormShell = <TValues,>({
  form,
  initialValues,
  canEdit,
  canCancel = false,
  isSaving = false,
  mode = 'edit',
  submittingLabel,
  submitLabel,
  cancelLabel,
  onSubmit,
  children,
}: EditableFormShellProps<TValues>) => {
  React.useEffect(() => {
    form.reset(initialValues);
  }, [initialValues, mode]);

  const isReadOnly = mode === 'read';

  // In edit mode we still disable fields if deps aren't loaded (canEdit === false)
  const disableAllFields = isReadOnly || isSaving || !canEdit;

  const handleCancel = () => {
    form.reset(initialValues);
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values);
  });

  const showSubmitCancel = mode === 'edit';

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <fieldset disabled={disableAllFields}>{children({ mode, canEdit })}</fieldset>

        {showSubmitCancel && (
          <div className="d-flex gap-2 mt-3">
            <Button type="submit" variant="success" disabled={isSaving || !canEdit}>
              {isSaving ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> {submittingLabel || t('common.saving')}
                </>
              ) : (
                <>{submitLabel || t('common.update')}</>
              )}
            </Button>

            {canCancel && (
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {cancelLabel || t('common.cancel')}
              </Button>
            )}
          </div>
        )}
      </form>
    </FormProvider>
  );
};
